/**
 * PDF Watermark Utilities
 * 
 * This module provides functions to add watermarks to PDF exports
 * for free plan users.
 */

/**
 * Add watermark overlay to HTML content for PDF export
 * This creates a diagonal watermark that appears when printing
 */
export function addWatermarkToHTML(htmlContent: string, planName: string | null | undefined): string {
    // Only add watermark for free plan users
    if (planName && planName !== 'free') {
        return htmlContent;
    }

    // Watermark text (escape HTML to prevent XSS)
    const watermarkText = 'LRDEnE Free Plan - Upgrade for watermark-free PDFs';
    const escapedText = watermarkText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    
    // Create watermark CSS
    const watermarkCSS = `
        <style>
            @media print {
                .watermark-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                    z-index: 9999;
                    opacity: 0.15;
                }
                .watermark-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(-45deg);
                    font-size: 48px;
                    font-weight: bold;
                    color: #666;
                    white-space: nowrap;
                    font-family: Arial, sans-serif;
                    letter-spacing: 2px;
                }
            }
            /* Hide watermark on screen, show only when printing */
            .watermark-overlay {
                display: none;
            }
            @media print {
                .watermark-overlay {
                    display: block;
                }
            }
        </style>
    `;

    // Create watermark HTML (with escaped text)
    const watermarkHTML = `
        <div class="watermark-overlay">
            <div class="watermark-text">${escapedText}</div>
        </div>
    `;

    // Insert watermark CSS into head and watermark HTML into body
    let modifiedHTML = htmlContent;
    
    // Add CSS to head if </head> exists, otherwise add before </html>
    if (modifiedHTML.includes('</head>')) {
        modifiedHTML = modifiedHTML.replace('</head>', `${watermarkCSS}</head>`);
    } else if (modifiedHTML.includes('<head>')) {
        modifiedHTML = modifiedHTML.replace('<head>', `<head>${watermarkCSS}`);
    } else {
        // If no head tag, add CSS before body or html
        if (modifiedHTML.includes('<body>')) {
            modifiedHTML = modifiedHTML.replace('<body>', `${watermarkCSS}<body>`);
        } else {
            modifiedHTML = `${watermarkCSS}${modifiedHTML}`;
        }
    }
    
    // Add watermark HTML to body
    if (modifiedHTML.includes('<body>')) {
        modifiedHTML = modifiedHTML.replace('<body>', `<body>${watermarkHTML}`);
    } else if (modifiedHTML.includes('</body>')) {
        modifiedHTML = modifiedHTML.replace('</body>', `${watermarkHTML}</body>`);
    } else {
        // If no body tag, append before closing html
        if (modifiedHTML.includes('</html>')) {
            modifiedHTML = modifiedHTML.replace('</html>', `${watermarkHTML}</html>`);
        } else {
            modifiedHTML = `${modifiedHTML}${watermarkHTML}`;
        }
    }

    return modifiedHTML;
}

/**
 * Check if user should have watermarked PDFs
 */
export function shouldWatermarkPDF(planName: string | null | undefined): boolean {
    return !planName || planName === 'free';
}

